/**
 * TabelEditorSheet — bottom-sheet редактор табеля за смену (§4.3).
 *
 * Заменяет inline-collapse `DeclaredEditor` (старая SECTION 2 в `FieldSignPage`).
 * Открывается по тапу «✎ Изменить» из value-блока «Табель». Внутри:
 *   - HoursStepper (шаг 0.5)
 *   - warning-блок «Переработка +N ч» (только когда `pending > shiftTodayHalf`)
 *   - кнопки «Отмена» / primary («Применить» или «Подтвердить переработку»)
 *
 * Контракт:
 *   - `pendingHalf` сбрасывается на `declaredHalf` при каждом `open=true`,
 *     чтобы повторное открытие после Apply показывало свежее значение.
 *   - «Отмена» закрывает sheet через `onOpenChange(false)`, `onApply` НЕ вызывается.
 *   - primary меняет текст derived от `pendingHalf > shiftTodayHalf`, без
 *     отдельного boolean'а `overtimeConfirmed`.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { HoursStepper } from "@/components/field/HoursStepper";
import { OvertimeBanner } from "@/components/timesheet/OvertimeBanner";
import { formatHoursHalf } from "@/lib/timesheet";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";

export interface TabelEditorSheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Текущее значение табеля в полу-часах (из родителя). */
  declaredHalf: number;
  /** Норма по производственному календарю в полу-часах. */
  shiftTodayHalf: number;
  /** Колбек при «Применить»/«Подтвердить переработку». Sheet закрывает родитель. */
  onApply: (nextHalf: number) => void;
}

export function TabelEditorSheet({
  open,
  onOpenChange,
  declaredHalf,
  shiftTodayHalf,
  onApply,
}: TabelEditorSheetProps): React.ReactElement {
  const { t } = useTranslation();

  // pendingHalf — локальный draft. Каждое открытие сбрасывает на актуальный
  // declaredHalf, чтобы не «застревать» после прошлой отмены / применения.
  // Реализуем reset-on-open через сравнение с предыдущим значением `open`
  // в render-фазе (React docs «Storing information from previous renders»)
  // — это корректно и не триггерит cascading-renders, в отличие от useEffect.
  const [pendingHalf, setPendingHalf] = React.useState<number>(declaredHalf);
  const [wasOpen, setWasOpen] = React.useState<boolean>(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setPendingHalf(declaredHalf);
  }

  const isOvertime = pendingHalf > shiftTodayHalf;
  useReleaseDirtyState(open && pendingHalf !== declaredHalf);

  const calendarHint = t("field.sign.tabelEdit.calendarHint", {
    hours: formatHoursHalf(shiftTodayHalf),
  });

  function handleApply(): void {
    onApply(pendingHalf);
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-[2px] p-0"
        data-testid="field-sign-tabel-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-2">
          <SheetTitle
            className="font-condensed text-[16px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: "var(--fg-1)", fontFamily: "var(--font-display)" }}
          >
            {t("field.sign.tabelEdit.title")}
          </SheetTitle>
          <SheetDescription
            className="font-condensed text-[11px] uppercase tracking-[0.18em]"
            style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
            data-testid="field-sign-tabel-sheet-hint"
          >
            {calendarHint}
          </SheetDescription>
        </SheetHeader>

        {/* Banner above stepper: появление/исчезание плашки растёт sheet
            ВВЕРХ, но расстояние «+/−» до низа экрана не меняется — палец
            не промахивается мимо stepper'а при последовательных тапах. */}
        <div className="mx-5 mt-1 mb-3">
          <OvertimeBanner
            normHoursHalf={shiftTodayHalf}
            actualHoursHalf={pendingHalf}
            data-testid="field-sign-tabel-sheet-overtime"
          />
        </div>

        <div className="px-5 pt-1 pb-5">
          <HoursStepper
            value={pendingHalf / 2}
            onChange={(next) => setPendingHalf(Math.round(next * 2))}
            min={0}
            max={24}
            showQuickSet={false}
            aria-label={t("field.sign.tabelEdit.stepperAria")}
            data-testid="field-sign-tabel-sheet-stepper"
          />
        </div>

        <div
          className="flex items-stretch gap-2 border-t px-5 pt-4"
          style={{
            borderColor: "var(--rule-line)",
            paddingBottom: "max(16px, env(safe-area-inset-bottom, 16px))",
          }}
        >
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-12 flex-1 items-center justify-center font-condensed text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors"
            style={{
              color: "var(--fg-1)",
              background: "var(--bg-panel-0)",
              border: "1px solid var(--rule-strong, var(--rule-line))",
              borderRadius: "var(--radius-button)",
              fontFamily: "var(--font-display)",
            }}
            data-testid="field-sign-tabel-sheet-cancel"
          >
            {t("field.sign.tabelEdit.cancel")}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="inline-flex h-12 flex-[2] items-center justify-center px-3 text-center font-condensed text-[13px] font-semibold uppercase tracking-[0.1em] leading-tight transition-colors"
            style={{
              color: "#fff",
              background: "var(--soot-900, #08090b)",
              borderRadius: "var(--radius-button)",
              fontFamily: "var(--font-display)",
            }}
            data-testid="field-sign-tabel-sheet-apply"
            data-overtime={isOvertime || undefined}
          >
            {isOvertime
              ? t("field.sign.tabelEdit.confirmOvertime")
              : t("field.sign.tabelEdit.apply")}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
